import { useId, useState, type FormEvent } from 'react';
import { BRAND_LIMITS, WORKSPACE_DETAIL_FIELDS, type CreateTenantDto, type Tenant, type WorkspaceNameConflict } from '@mg/shared';
import { useAuth } from '../../../auth/AuthContext';
import { useTenant } from '../../../auth/TenantContext';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/Field';
import { WorkspaceNameConflictNotice } from '../../../components/workspace/WorkspaceNameConflictNotice';
import { actionErrorText, api, asWorkspaceNameConflict } from '../../../lib/api';
import { useWebsiteField } from '../../brand/website';
import { CREATE_COPY } from '../wizardCopy';

/**
 * The body of a Brand Workspace v2 Create: the name and the website, nothing else (final-design §2.14;
 * any legacy brand field would be a 400 BRAND_FIELDS_MOVED). The intersection keeps this compiling against
 * the shared CreateTenantDto both before and after it gains `websiteUrl` and an optional `description`.
 */
function createBody(name: string, websiteUrl: string): CreateTenantDto {
  const body = { name, websiteUrl };
  return body as typeof body & CreateTenantDto;
}

/**
 * Create Brand Workspace while Brand Workspace v2 is on (spec:38-50; web.md §2.4): the name and the
 * public website, nothing else. Create is enabled once the name is filled in and the website is a valid
 * address; the website is sent in its normalized `https://` form.
 *
 * On success: the workspace list is read again (the new slug must be in it before a URL names it, or
 * TenantProvider would put the old workspace back), then the signed-in user (their new Workspace Admin
 * membership opens Brand Setup to them), then `onCreated`. It does not switch the active workspace
 * itself: the caller navigates to a URL that names it. Used by the Create page and the first-login screen.
 */
export function CreateBrandWorkspaceForm({
  onCreated,
  onCancel,
  submitLabel = CREATE_COPY.submit,
}: {
  onCreated: (tenant: Tenant) => void;
  /** Omitted on the first-login screen, which cannot be dismissed. */
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const { refresh } = useTenant();
  const { refresh: refreshPrincipal } = useAuth();

  const baseId = useId();
  const nameId = `${baseId}-name`;
  const websiteId = `${baseId}-website`;
  const websiteErrorId = `${baseId}-website-error`;

  const [name, setName] = useState('');
  const website = useWebsiteField('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<WorkspaceNameConflict | null>(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName !== '' && website.normalized !== null && !submitting;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || website.normalized === null) return;
    setSubmitting(true);
    setError(null);
    setConflict(null);
    try {
      const created = await api.createTenant(createBody(trimmedName, website.normalized));
      await refresh();
      await refreshPrincipal();
      onCreated(created);
    } catch (err) {
      const nameConflict = asWorkspaceNameConflict(err);
      if (nameConflict) setConflict(nameConflict);
      else setError(actionErrorText(err, CREATE_COPY.failed));
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <FormField label={WORKSPACE_DETAIL_FIELDS.name.label} htmlFor={nameId} required>
        <Input
          id={nameId}
          value={name}
          onChange={(e) => {
            setConflict(null);
            setName(e.target.value);
          }}
          placeholder={CREATE_COPY.namePlaceholder}
          autoFocus
          required
          maxLength={BRAND_LIMITS.workspaceName}
        />
      </FormField>

      <FormField label={WORKSPACE_DETAIL_FIELDS.websiteUrl.label} htmlFor={websiteId} required>
        <Input
          id={websiteId}
          // Text, not url: an address without https:// is accepted (and normalized), so the browser's
          // own URL check must not refuse it first.
          type="text"
          inputMode="url"
          autoComplete="url"
          value={website.value}
          onChange={(e) => website.setValue(e.target.value)}
          onBlur={website.onBlur}
          placeholder={CREATE_COPY.websitePlaceholder}
          required
          aria-invalid={website.error ? true : undefined}
          aria-describedby={website.error ? websiteErrorId : undefined}
        />
        {website.error && (
          <p id={websiteErrorId} className="mt-1 text-xs text-red-600 dark:text-error-dark">
            {website.error}
          </p>
        )}
      </FormField>

      {conflict && <WorkspaceNameConflictNotice conflict={conflict} />}

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            {CREATE_COPY.cancel}
          </Button>
        )}
        <Button type="submit" loading={submitting} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
